import axios from 'axios';
import { API_ENDPOINTS } from '@/constants';

// Send cookies (httpOnly auth cookie) with every request
axios.defaults.withCredentials = true;

// Handle 401 responses (token expired or invalid)
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    const url = error.config?.url || '';
    // Don't redirect for auth endpoints — AuthContext handles those gracefully
    const isAuthEndpoint = url.includes('/auth/');
    if (error.response?.status === 401 && !isAuthEndpoint) {
      localStorage.removeItem('auth_user');
      window.location.href = '/';
    }
    return Promise.reject(error);
  }
);

// Course API
export const courseAPI = {
  create: async (options = {}) => {
    const response = await axios.post(API_ENDPOINTS.COURSES, {
      title: options.title || "Untitled Course",  // Provide default title to prevent empty courses
      language: options.language || "",  // Course content language
      interactionLanguage: options.interactionLanguage || "",  // UI/chat language (defaults to language if empty)
    });
    return response.data;
  },

  getById: async (courseId) => {
    const response = await axios.get(`${API_ENDPOINTS.COURSES}/${courseId}`);
    return response.data;
  },

  update: async (courseId, updates) => {
    const response = await axios.patch(`${API_ENDPOINTS.COURSES}/${courseId}`, updates);
    return response.data;
  },

  delete: async (courseId) => {
    const response = await axios.delete(`${API_ENDPOINTS.COURSES}/${courseId}`);
    return response.data;
  },

  createCheckpoint: async (courseId, label, stepIndex, chatHistory) => {
    const response = await axios.post(`${API_ENDPOINTS.COURSES}/${courseId}/checkpoint`, {
      label,
      stepIndex,
      chatHistory,
    });
    return response.data;
  },

  restoreCheckpoint: async (courseId, versionId) => {
    const response = await axios.post(`${API_ENDPOINTS.COURSES}/${courseId}/restore/${versionId}`);
    return response.data;
  },

  exportMoodle: async (courseId) => {
    const url = API_ENDPOINTS.MOODLE_EXPORT(courseId);
    const response = await axios.get(url, { responseType: 'blob' });
    return response;
  },

  exportEclass: async (courseId, source) => {
    const url = API_ENDPOINTS.ECLASS_EXPORT(courseId);
    const response = await axios.post(url, { source });
    return response.data;
  },

};

// Chat API (New Agent-Based)
export const chatAPI = {
  sendMessage: async (courseId, message, stepIndex) => {
    // Use step-specific endpoint to ensure correct agent handles the message
    const endpoint = `${API_ENDPOINTS.CHAT}/step${stepIndex}`;
    const response = await axios.post(endpoint, {
      courseId,
      message,
    });
    return response.data;
  },

  generateActivities: async (courseId) => {
    const response = await axios.post(`${API_ENDPOINTS.CHAT}/generate-activities`, {
      courseId,
    });
    return response.data;
  },

  step3Part1: async (courseId) => {
    const response = await axios.post(`${API_ENDPOINTS.CHAT}/step3-part1/${courseId}`);
    return response.data;
  },

  step3Part2: async (courseId) => {
    const response = await axios.post(`${API_ENDPOINTS.CHAT}/step3-part2/${courseId}`);
    return response.data;
  },

  interactiveActivity: async (courseId, message, stepIndex) => {
    const response = await axios.post(`${API_ENDPOINTS.CHAT}/interactive-activity`, {
      courseId,
      message,
      stepIndex,
    });
    return response.data;
  },

  commitSession: async (courseId) => {
    const response = await axios.post(API_ENDPOINTS.CHAT_COMMIT, {
      courseId,
    });
    return response.data;
  },

  completeStep: async (courseId, stepIndex) => {
    const response = await axios.post(API_ENDPOINTS.CHAT_STEP_COMPLETE, {
      courseId,
      stepIndex,
    });
    return response.data;
  },

  getSessionStats: async () => {
    const response = await axios.get(API_ENDPOINTS.CHAT_SESSION_STATS);
    return response.data;
  },
};

// Legacy AI API (for backward compatibility - will be removed)
export const aiAPI = {
  chat: async (courseId, message, stepIndex) => {
    // Redirect to new chat API
    return chatAPI.sendMessage(courseId, message, stepIndex);
  },

  generateGoals: async (courseId) => {
    throw new Error('generateGoals is deprecated. Use chat API instead.');
  },

  generateActivities: async (courseId) => {
    throw new Error('generateActivities is deprecated. Use chat API instead.');
  },

  transform: async (courseId, action) => {
    throw new Error('transform is deprecated. Use chat API instead.');
  },
};

// Misc API
export const systemAPI = {
  health: async () => {
    const response = await axios.get(`${API_ENDPOINTS.BASE.replace(/\/$/, '')}/../health`);
    return response.data;
  },
};

